import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AccountingApi } from '_api';
import { handleError, handleResponse } from '_helpers/api';

export const getFileTypes = createAsyncThunk(
  'fileTypes/getFileTypes',
  async function ({ customerId, productId }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.getFileTypes({
        customerId,
        productId,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const getMergedFileTypeMap = createAsyncThunk(
  'fileTypes/getMergedFileTypeMap',
  async function ({ customerId, productIds = [] }, { rejectWithValue }) {
    try {
      const getMapById = async (productId) => {
        const response = await AccountingApi.getFileTypes({ customerId, productId });
        const data = handleResponse(response);

        return data.reduce((res, { code, value }) => {
          res[code] = value;
          return res;
        }, {});
      };

      const result = await Promise.all(productIds.map((productId) => getMapById(productId)));

      const fileTypeMap = result.reduce((res, valueMap) => {
        return { ...res, ...valueMap };
      }, {});

      return fileTypeMap;
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

const fileTypesSlice = createSlice({
  name: 'fileTypes',
  initialState: {
    data: [],
    error: null,
    loading: false,
    fileTypeMap: {
      data: {},
      error: null,
      loading: false,
    },
  },
  reducers: {
    resetData(state) {
      state.data = [];
    },
    resetError(state) {
      state.error = null;
    },
    resetMergedFileTypeMapData(state) {
      state.fileTypeMap.data = {};
    },
    resetMergedFileTypeMapError(state) {
      state.fileTypeMap.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFileTypes.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getFileTypes.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getFileTypes.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(getMergedFileTypeMap.pending, (state) => {
        state.fileTypeMap.error = null;
        state.fileTypeMap.loading = true;
      })
      .addCase(getMergedFileTypeMap.fulfilled, (state, action) => {
        state.fileTypeMap.data = action.payload;
        state.fileTypeMap.loading = false;
      })
      .addCase(getMergedFileTypeMap.rejected, (state, action) => {
        state.fileTypeMap.error = action.payload;
        state.fileTypeMap.loading = false;
      });
  },
});

export const { resetData, resetError, resetMergedFileTypeMapData, resetMergedFileTypeMapError } =
  fileTypesSlice.actions;

export default fileTypesSlice.reducer;
